import {memo, useLayoutEffect, useRef} from "react";
import {Link} from "react-router-dom";
import type {CategoryEntry, CategoryGroupEntry, GroupRepo} from "../../models/GroupRepo";
import {Col, css, Row, Text} from "../../ui/Box";
import {findNearestIndexViaBinarySearch} from "../../utils";
import {findClosestScrollableParent} from "../../utils/dom-utils";
import {useDndItem, useDndList} from "../../utils/list-dnd/use-dnd-list";
import {createSorter, moveAndEnsureBalance} from "../../utils/sort-utils";

type CategoryTileProps = {
  cat: CategoryEntry;
  startPx: number;
  index: number;
};
const CategoryTile = memo((props: CategoryTileProps) => {
  const {cat, index, startPx} = props;
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const {handlers, transform} = useDndItem({nodeRef, index, startPx, key: cat.id});

  return (
    <Row
      sp="16"
      align="baseline"
      surface
      px="8"
      py="8"
      size="16"
      style={{transform}}
      absolute
      top="0"
      left="0"
      width="100%"
      rounded="4"
      {...handlers}
      ref={nodeRef}
    >
      <Text whiteSpace="nowrap">
        {/* lingui-extract-ignore */}
        {cat.emoji} {cat.name}
      </Text>
      <Link
        to={`edit/${cat.id}`}
        className={css({size: "12", color: "secondary", display: "block"})}
      >
        Edit
      </Link>
    </Row>
  );
});

const LIST_GAP = 4;

type CategoryListProps = {
  catsWithEntries: {cat: CategoryEntry; catGroupEntry: CategoryGroupEntry}[];
  groupRepo: GroupRepo;
};
const CategoryList = (props: CategoryListProps) => {
  const {catsWithEntries, groupRepo} = props;
  const nodeRef = useRef<HTMLElement | null>(null);
  const scrollNodeRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    // This approach was chosen over passing a ref from the parent as
    // useVirtual uses useLayoutEffect, and children's useLayoutEffect are
    // called _before_ the parent gets a chance to set refs (both via useRef
    // and callback refs)
    scrollNodeRef.current = findClosestScrollableParent(nodeRef.current!) as HTMLElement;
  }, []);

  const {height, virtualItems, scrollMargin} = useDndList({
    listContainerRef: nodeRef,
    count: catsWithEntries.length,
    getScrollElement: () => scrollNodeRef.current,
    gap: LIST_GAP,
    height: 18 + 24,
    onDrop: ({targetIndex, startIndex}) => {
      if (targetIndex === startIndex) return;
      let before;
      let after;
      if (startIndex > targetIndex) {
        // move up
        before = targetIndex > 0 ? catsWithEntries[targetIndex - 1] : null;
        after = catsWithEntries[targetIndex];
        // console.log("MOVE UP", before?.sortIndex, after.sortIndex);
      } else {
        before = catsWithEntries[targetIndex];
        after = targetIndex < catsWithEntries.length - 1 ? catsWithEntries[targetIndex + 1] : null;
      }
      const updateEls = moveAndEnsureBalance({
        targetEl: catsWithEntries[startIndex],
        prevEl: before,
        nextEl: after,
        getSortIndex: (el) => el.catGroupEntry.sortIndex,
        sorter: createSorter(),
        getSortedElementsBetween: (start, end) => {
          const [startIdx] = findNearestIndexViaBinarySearch(
            catsWithEntries,
            start,
            (cat) => cat.catGroupEntry.sortIndex
          );
          const [, endIdx] = findNearestIndexViaBinarySearch(
            catsWithEntries,
            end,
            (cat) => cat.catGroupEntry.sortIndex
          );
          const els = catsWithEntries.slice(
            Math.max(0, startIdx),
            Math.min(catsWithEntries.length - 1, endIdx) + 1
          );
          return els;
        },
      });
      groupRepo.store.transaction(() => {
        updateEls.forEach(([cat, sortIndex]) => {
          groupRepo.CategoryGroupEntries.patch(cat.catGroupEntry.id, {
            sortIndex,
          });
        });
      });
    },
  });

  return (
    <Col relative style={{height}} ref={nodeRef}>
      {virtualItems.map((item) => {
        const {cat} = catsWithEntries[item.index];
        return (
          <CategoryTile
            key={cat.id}
            cat={cat}
            index={item.index}
            startPx={item.start - scrollMargin}
          />
        );
      })}
    </Col>
  );
};

export default CategoryList;
